'use strict';

var extend = require('extend');
var ComponentManager = require('../lib/ComponentManager.js');
var Debouncer = require('../lib/Debouncer.js');
var AFM = require('../lib/animate/FrameManager.js');
var ElementTools = require('../lib/core/ElementTools.js');
var isVideoAutoplaySupported = require('../lib/isVideoAutoplaySupported.js');

var settingDefaults = {
	"autoplayandpause" : false,
	"playsinline" : false,
	"muted" : false,
	"loop" : false,
	"preload" : "metadata",
	"autosize" : true
};

var PlayStates = {
	Paused : 0,
	Pausing : 1,
	Starting : 2,
	Playing : 3
};

// TODO Consider stopping videos if tab or browser is not focused. See: https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API?redirectlocale=en-US&redirectslug=DOM%2FUsing_the_Page_Visibility_API

/**
 * @class
 * List your sources in order of preference: Preferred format, then from largest to smallest dimensions.
 * The sources will be checked from top to bottom. The last compatible option found will be used.
 */
var Video = function(containerElement) {
	isVideoAutoplaySupported(); // Will add a style class to the HTML tag to indicate support for autoplay or not (e.g. videoAutoplaySupported)

	// Load json data from the web page (it is printed inline as a child of videoElement)
	var jsonElement = containerElement.querySelector(".video__json");
	if (jsonElement !== null) {
		var data = JSON.parse(jsonElement.textContent.replace("/*<![CDATA[*/", "").replace("/*]]>*/", ""));
		this._sources = data.sources;
		if (this._sources === undefined || this._sources.length === 0) {
			// console.warn("No video sources provided. Not doing any further setup.");
			return;
		}
		this._settings = extend(true, {}, settingDefaults, data.settings);
	} else {
		console.warn("Can not setup video because configuration is missing.");
		return;
	}

	// Save references to elements
	this._elements = {
		container: containerElement,
		saveSpace: containerElement.querySelector(".video__saveSpace")
	};

	// Init a few starting variables and bind a few functions to this instance
	this._isVideoReady = false;
	this._playState = undefined; // one of PlayStates
	this._isAutoPause = undefined; // set to true when in the process of pausing and it was triggered by an autopause event

	this.play = this.play.bind(this);
	this.pause = this.pause.bind(this);

	this._setInView = this._setInView.bind(this);
	this._onCanPlay = this._onCanPlay.bind(this);
	this._onPlay = this._onPlay.bind(this);
	this._onPause = this._onPause.bind(this);
	this._onPlayPauseClick = this._onPlayPauseClick.bind(this);
	this._resizeVideo = this._resizeVideo.bind(this);
	this._resizeVideoWrite = this._resizeVideoWrite.bind(this);
	this._updateSource = this._updateSource.bind(this);

	// Setup controls
	var playPauseELem = containerElement.querySelector(".video__playPause");
	if (playPauseELem !== null && this._settings.controls !== false) {
		playPauseELem.addEventListener("click", this._onPlayPauseClick, false);
	}

	// Update source, and setup listener to update when window scales up
	window.addEventListener("resize", (new Debouncer(this._updateSource, 2000, false)).run, false);
	if (this._settings.fit !== undefined) {
		window.addEventListener("resize", (new Debouncer(this._resizeVideo, "read")).run, false);
	}
	this._updateSource();

	// Setup observer so video is automatically played and paused based on when in view.
	getViewportObserver().observe(this._elements.container);
}



/* ===================================================================
		Static methods and properties on Video
=================================================================== */
/**
 * @public
 */
Video.COMPONENT_STYLE_CLASS = "video";

/**
 * @public
 */
var _canPlayTypeTestVideo;
Video.canPlayType = function(type) {
	if (_canPlayTypeTestVideo === undefined) {
		_canPlayTypeTestVideo = document.createElement("video");
	}
	if ("canPlayType" in _canPlayTypeTestVideo === true) {
		var playable = _canPlayTypeTestVideo.canPlayType(type).toLowerCase();
		return (playable === "maybe") || (playable === "probably");
	}
	return undefined;
}



/* ===================================================================
		Protype methods and properties on Video
=================================================================== */
/**
 * @public
 */
Video.prototype.destroy = function() {
	// Delete DOM references
	delete this._elements;
	delete this._sources;
	delete this._settings;
	// TODO Remove observers and other stuff. Any listeners to remove?
}

/**
 * @public
 */
Video.prototype._updateSource = function() {
	var videoContainerBCR = this._elements.container.getBoundingClientRect();
	var width = videoContainerBCR.width;
	var height = videoContainerBCR.height;
	if (
		(this._updateSourceLastWidth === undefined || this._updateSourceLastHeight === undefined)
		|| (this._updateSourceLastWidth < width || this._updateSourceLastHeight < height)
	) {
		var bestSource = this.getBestSource(width, height);
		var updated = this.setSource(bestSource, true);
	}
	this._updateSourceLastWidth = width;
	this._updateSourceLastHeight = height;
}

/**
 * @public
 */
Video.prototype.setSource = function(source, maintainState) {
	if (this._videoElement === undefined) {
		var video = document.createElement("video");
		video.classList.add("video__video");

		video.autoplay = this._settings.autoplayandpause;
		if (this._settings.playsinline) {
			video.setAttribute("playsinline", "playsinline");
		}
		video.muted = this._settings.muted;
		video.loop = this._settings.loop;
		video.preload = this._settings.preload;

		// Position if fit is set.
		if (this._settings.fit !== undefined) {
			var x = this._settings.fit.x * 100;
			var y = this._settings.fit.y * 100;
			video.style.left = (x) + "%";
			video.style.top = (y) + "%";
			ElementTools.setTransform(video, 
				"translate(" + (-1 * x) + "%, " + (-1 * y) + "%)"
			);
		}

		video.addEventListener("canplay", this._onCanPlay);
		this._isVideoReady = false;
		
		video.addEventListener("play", this._onPlay, false);
		video.addEventListener("pause", this._onPause, false);

		// TODO Add more listners and record events in Google Analytics. Make sure to differentiate between manual and automatic play/pause.
		
		video.src = source == undefined ? undefined : source.src;

		this._elements.container.appendChild(video);
		this._videoElement = video;
		this._videoElementSource = source;
		return true;
	} else {
		if (this._videoElementSource.src !== source.src) {
			this._isVideoReady = false;
			var currentTime = this._videoElement.currentTime;
			this._videoElement.src = source.src;
			this._videoElementSource = source;
			if (maintainState === true) {
				this._videoElement.currentTime = currentTime;
			} else {
				this._setPlayState(PlayStates.Paused);
			}
			return true;
		}
	}
	return false;
}

/**
 * @public
 */
Video.prototype.getBestSource = function(containerWidth, containerHeight) {
	
	var bestFitSource, videoType;

	for (var i=0, len = this._sources.length, source; i < len; i++) {
		source = this._sources[i];

		if (videoType === undefined && Video.canPlayType(source.type)) {
			videoType = source.type;
		}

		if (videoType === undefined) {
			continue;
		}

		if (
			(videoType === source.type)
			&& (bestFitSource === undefined
				|| ((source.width >= containerWidth || source.width === null || source.width === undefined)
					&& (source.height >= containerHeight || source.height === null || source.height === undefined))
			)
		) {
			bestFitSource = source;
		} else {
			// This one didn't work so we use the last one that did work.
			break;
		}
	}
	return bestFitSource;
}

/**
 * @public
 */
Video.prototype.play = function() {
	this._setPlayState(PlayStates.Starting);

	var promise = this._videoElement.play();
	if (promise !== undefined) {
		promise.catch(function(error) {
			console.error("Could not play video.", error);
		});
	}
}

/**
 * @public
 */
Video.prototype.pause = function(isAutoPause) {
	isAutoPause = isAutoPause === true;
	this._setPlayState(
		this._playState === PlayStates.Playing ? PlayStates.Pausing : PlayStates.Paused,
		isAutoPause
	);
	this._videoElement.pause();
}

/**
 * @private
 */
Video.prototype._onPlayPauseClick = function(event) {
	if (this._playState <= PlayStates.Pausing) {
		this.play();
	} else {
		this.pause();
	}
}

/**
 * @private
 */
Video.prototype._resizeVideo = function() {
	if (this._settings.fit === undefined) {
		return;
	}

	// Determine dimensions
	var containerRatio = this._elements.container.clientWidth / this._elements.container.clientHeight;
	var videoRatio = this._videoElement.videoWidth / this._videoElement.videoHeight;
	var useMaxWidth = this._settings.fit.type === "cover" ? videoRatio < containerRatio : videoRatio > containerRatio;
	if (useMaxWidth) {
		this._resizeVideoElementWidth = (this._elements.container.clientWidth + 2) + "px";
		this._resizeVideoElementHeight = "auto";
	} else {
		this._resizeVideoElementWidth = "auto";
		this._resizeVideoElementHeight = (this._elements.container.clientHeight + 2) + "px";
	}

	AFM.once("write", this._resizeVideoWrite);
}

/**
 * @private
 */
Video.prototype._resizeVideoWrite = function() {
	if (this._videoElement !== undefined && this._resizeVideoElementWidth !== undefined) {
		this._videoElement.style.width = this._resizeVideoElementWidth;
		this._videoElement.style.height = this._resizeVideoElementHeight;
		this._resizeVideoElementWidth = undefined;
		this._resizeVideoElementHeight = undefined;
	}
}

/**
 * @private
 * This is for record purposes only. It does not affect the actual play vs pause of the video.
 */
Video.prototype._setPlayState = function(state, isAutoPausing) {
	this._playState = state;

	// Save info
	if (isAutoPausing !== undefined) {
		this._isAutoPause = isAutoPausing;
	}

	// Clean up
	if (
		this._playState === PlayStates.Playing
		|| this._playState === PlayStates.Paused
	) {
		this._elements.container.classList.remove("isPlaying");
		this._elements.container.classList.remove("isAutoPaused");
		this._elements.container.classList.remove("isManualPaused");
	}

	// Add appropriate state class
	if (this._playState === PlayStates.Playing) {
		this._elements.container.classList.add("isPlaying");
	} else if (this._playState === PlayStates.Paused) {
		if (isAutoPausing === true || (isAutoPausing === undefined && this._isAutoPause === true)) {
			this._elements.container.classList.add("isAutoPaused");
		} else {
			this._elements.container.classList.add("isManualPaused");
		}
	}
}

/**
 * @private
 */
Video.prototype._onCanPlay = function(event) {
	if (this._isVideoReady !== true) {
		this._isVideoReady = true;

		this._elements.container.classList.add("isReady");

		// Update the element that saves space and the max-width of the container
		if (this._elements.saveSpace !== null && this._settings.autosize === true) {
			var sizeRatio = this._videoElement.videoWidth / this._videoElement.videoHeight;
			this._elements.saveSpace.style.paddingBottom = ((1 / sizeRatio) * 100) + "%";
			this._elements.container.style.maxWidth = "calc(" + (sizeRatio * 98) + "vh - 30px)";
		}

		// Resize video
		this._resizeVideo();

		// Play or pause based, if the saved play state is waiting for that to happen (could have been called while the video was loading)
		// Also needed for when the video is switched so the new video is playing if the previous version was.
		if (this._playState >= PlayStates.Starting) {
			this.play();
		} else if (this._playState <= PlayStates.Pausing) {
			this.pause(this._isAutoPause);
		}
	}
}

/**
 * @private
 */
Video.prototype._onPlay = function(event) {
	this._setPlayState(PlayStates.Playing);
}

/**
 * @private
 */
Video.prototype._onPause = function(event) {
	this._setPlayState(PlayStates.Paused);
}

/**
 * @private
 */
Video.prototype._setInView = function(isInView) {
	this._isInView = isInView;
	if (this._settings.autoplayandpause === true) {
		if (isInView === true) {
			if (this._isAutoPause !== false) {
				this.play();
			}
		} else {
			if (this._playState > PlayStates.Pausing || this._playState === undefined) {
				this.pause(true);
			}
		}
	}
}



/* ===================================================================
		Private methods and properties not on Video
=================================================================== */

var _viewportObserver;
var _threshold = 0.25;
/**
 * @private
 */
function getViewportObserver(entries, observer) {
	if (_viewportObserver === undefined) {
		_viewportObserver = new IntersectionObserver(
			_onObserve,
			{
				root : null, // null results in viewport
				rootMargin : '0px',
				threshold : _threshold
			}
		);
	}
	return _viewportObserver;
}

/**
 * @private
 */
function _onObserve(entries, observer) {
	for (var i=0, entry, elem; i < entries.length; i++) {
		entry = entries[i];
		elem = entry.target;

		var componentManager = ComponentManager.getInstance();
		var video = componentManager.getInstanceForElement(elem, Video.COMPONENT_STYLE_CLASS);
		
		if (video !== undefined) {
			if (entry.intersectionRatio >= _threshold) {
					// Line above: Inline polyfill for Edge 15 lack of isIntersecting support. The
					// 		 polyfill we have should fix this if is loaded, but we don't load it for Edge 15.
				video._setInView(true);
			} else {
				video._setInView(false);
			}
		}
	}
}



module.exports = Video;

